import { useState } from "react";
import { Mail, Globe, Instagram, Linkedin, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { SEO } from "@/components/SEO";
import { Reveal } from "@/components/Reveal";
import api from "@/lib/api";

export default function Contact() {
  const [form, setForm] = useState({ full_name: "", email: "", business_name: "", service_category: "", message: "", preferred_contact: "Email", hp: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const inputCls = "w-full bg-ivory border border-champagne/40 rounded-lg px-4 py-3 text-espresso focus:outline-none focus:ring-2 focus:ring-champagne focus:border-transparent placeholder:text-espresso/40";

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contacts", form);
      setDone(true);
      toast.success("Message sent. We'll reply soon.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <SEO title="Contact" description="Get in touch with people, redefined. All services are provided virtually." />

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 md:pt-24 pb-24 grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-5">
          <Reveal>
            <span className="text-xs uppercase tracking-[0.25em] text-gold font-medium">Contact</span>
            <h1 className="font-serif text-5xl md:text-6xl text-espresso mt-5 leading-tight">Say hello.</h1>
            <p className="text-lg text-espresso/75 mt-6 leading-relaxed">
              Have a question or a project in mind? Reach out — we'd love to hear from you.
            </p>
            <div className="mt-10 space-y-5">
              <div className="flex items-center gap-4">
                <div className="w-11 h-11 rounded-full border border-champagne/50 flex items-center justify-center"><Mail className="text-espresso" size={18} /></div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-espresso/50">Email</p>
                  <a href="mailto:contact@peopleredefined.com" className="text-espresso font-medium hover:text-champagne transition-colors">contact@peopleredefined.com</a>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-11 h-11 rounded-full border border-champagne/50 flex items-center justify-center"><Globe className="text-espresso" size={18} /></div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-espresso/50">Delivery</p>
                  <p className="text-espresso font-medium">All services provided 100% virtually</p>
                </div>
              </div>
            </div>
            <div className="flex gap-3 mt-8">
              <a href="#" aria-label="Instagram" className="w-11 h-11 rounded-full bg-espresso text-ivory flex items-center justify-center hover:bg-forest transition-colors"><Instagram size={18} /></a>
              <a href="#" aria-label="LinkedIn" className="w-11 h-11 rounded-full bg-espresso text-ivory flex items-center justify-center hover:bg-forest transition-colors"><Linkedin size={18} /></a>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          {done ? (
            <Reveal>
              <div data-testid="contact-success" className="border border-champagne/40 rounded-2xl p-12 text-center">
                <Check className="mx-auto text-champagne" size={40} />
                <h3 className="font-serif text-3xl text-espresso mt-4">Message received.</h3>
                <p className="text-espresso/70 mt-3">Thank you for reaching out. We'll get back to you shortly.</p>
              </div>
            </Reveal>
          ) : (
            <Reveal delay={0.1}>
              <form onSubmit={submit} data-testid="contact-form" className="border border-champagne/30 rounded-2xl p-8 md:p-10 grid md:grid-cols-2 gap-5">
                <input type="text" value={form.hp} onChange={(e) => set("hp", e.target.value)} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden />
                <div>
                  <label className="block text-sm font-medium text-espresso mb-2">Full name</label>
                  <input required value={form.full_name} onChange={(e) => set("full_name", e.target.value)} className={inputCls} data-testid="contact-name" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-espresso mb-2">Email</label>
                  <input type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls} data-testid="contact-email" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-espresso mb-2">Business name (optional)</label>
                  <input value={form.business_name} onChange={(e) => set("business_name", e.target.value)} className={inputCls} data-testid="contact-business" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-espresso mb-2">Service category</label>
                  <select value={form.service_category} onChange={(e) => set("service_category", e.target.value)} className={inputCls} data-testid="contact-category">
                    <option value="">Select…</option>
                    <option>People Advisory</option><option>Talent Acquisition</option><option>Career Atelier</option><option>General Inquiry</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-espresso mb-2">Message</label>
                  <textarea required rows={5} value={form.message} onChange={(e) => set("message", e.target.value)} className={inputCls} data-testid="contact-message" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-espresso mb-2">Preferred contact method</label>
                  <div className="flex gap-3">
                    {["Email", "Phone"].map((m) => (
                      <button type="button" key={m} onClick={() => set("preferred_contact", m)} data-testid={`contact-pref-${m}`}
                        className={`rounded-full px-5 py-2 text-sm border transition-colors ${form.preferred_contact === m ? "bg-espresso text-ivory border-espresso" : "bg-ivory text-espresso border-champagne/40"}`}>
                        {m}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="md:col-span-2">
                  <button type="submit" disabled={loading} data-testid="contact-submit" className="inline-flex items-center gap-2 bg-espresso text-ivory rounded-full px-8 py-4 font-medium hover:bg-forest transition-colors disabled:opacity-60">
                    {loading && <Loader2 className="animate-spin" size={18} />} Send Message
                  </button>
                </div>
              </form>
            </Reveal>
          )}
        </div>
      </section>
    </div>
  );
}
